import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from 'src/shared/hooks/use-toast'

import { queryKeys } from '../db/queryKeys'
import type {
  GenerateMockupsDesign,
  PlacementMode,
} from './buildGenerateMockupsPayload'
import { ServerRoutes } from './serverRoutes'

export type GenerateMockupsProps = {
  quote_sub_item_id: number
  quote_item_id: number
  product_name: string
  decoration_name: string | null
  product_image_url: string | null
  logo_url: string | null
  product_color?: string | null
  placement_mode?: PlacementMode
  custom_prompt?: string | null
  design?: GenerateMockupsDesign
}

export const useGenerateMockups = () => {
  const toast = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (props: GenerateMockupsProps) => {
      const body: Record<string, unknown> = {
        quote_sub_item_id: props.quote_sub_item_id,
        product_name: props.product_name,
        decoration_name: props.decoration_name,
        product_image_url: props.product_image_url,
        logo_url: props.logo_url,
        placement_mode: props.placement_mode ?? 'manual',
      }

      if (props.custom_prompt) {
        body.custom_prompt = props.custom_prompt
      }

      if (props.product_color) {
        body.product_color = props.product_color
      }

      if (props.design) {
        body.design = props.design
      }

      const response = await fetch(
        import.meta.env.VITE_BE_SERVER_URL + ServerRoutes.generateMockups,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      )

      const responseBody = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(responseBody.msg || 'Failed to generate mockups')
      }
      return responseBody
    },
    onSuccess: (data, variables) => {
      if (data?.mockup_urls?.length) {
        queryClient.setQueriesData(
          { queryKey: [queryKeys.quoteItemById, variables.quote_item_id] },
          (old: unknown) => {
            if (!old || typeof old !== 'object') return old
            const item = old as {
              quote_sub_item?: Array<{ id: number; mockup_urls?: string[] }>
            }
            return {
              ...item,
              quote_sub_item: item.quote_sub_item?.map((sub) =>
                sub.id === variables.quote_sub_item_id
                  ? { ...sub, mockup_urls: data.mockup_urls }
                  : sub
              ),
            }
          }
        )
      }
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteItemById, variables.quote_item_id],
      })
      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItems] })
      toast.toast({
        title: 'Mockups generated',
        variant: 'success',
      })
    },
    onError: (error: Error) => {
      toast.toast({
        title: 'Something went wrong',
        description: error.message,
        variant: 'destructive',
        duration: 5000,
      })
    },
  })

  return mutation
}
